import { Employee, WorkSchedule, MonthlyPayrollData, WeeklyHolidayDetail } from '../types/employee';
import { TimeRange } from '../types/workTimeTemplate';
import employeeTaxTable from '../../.cursor/commands/json/employee_data.json';
import { breakTimeStringToHours, breakTimeStringToMinutes } from './breakTime';
import {
  STANDARD_WORK_MINUTES_PER_DAY,
  NIGHT_START_HOUR,
  NIGHT_END_HOUR,
  MAX_WORK_MINUTES_PER_SEGMENT,
  isPublicHoliday,
} from '../constants/laborLaw';
import { useRatesStore } from '../store/ratesStore';
import {
  calculateLaborDayBreakdown,
  calculateScheduleWorkedHours,
  isLaborDay,
  monthHasLaborDay,
  resolveEffectiveRate,
  type LaborDayAllowanceBreakdown,
} from './laborDayAllowance';

// 원천세 계산을 위한 타입 정의
interface TaxTableEntry {
  [key: string]: string;
}

// 총 근무 시간 계산 (분 단위 정밀도)
// 템플릿 기반 스케줄(workTime1/workTime2)과 기존 스케줄(start_time/end_time) 모두 지원
export const calculateTotalWorkHours = (schedules: WorkSchedule[]): number => {
  let totalMinutes = 0;
  
  schedules.forEach(schedule => {
    // 템플릿 기반 스케줄 (새 구조) 우선 처리
    if (schedule.workTime1 || schedule.workTime2) {
      if (schedule.workTime1) {
        const hours1 = getHoursBetween(schedule.workTime1.start, schedule.workTime1.end);
        totalMinutes += hours1 * 60;
      }
      
      if (schedule.workTime2) {
        const hours2 = getHoursBetween(schedule.workTime2.start, schedule.workTime2.end);
        totalMinutes += hours2 * 60;
      }
      
      // 템플릿 기반 스케줄의 휴게시간 차감
      totalMinutes -= breakTimeStringToMinutes(schedule.break_time);
    } else if (schedule.start_time && schedule.end_time) {
      // 기존 구조 (start_time/end_time)
      const startTime = new Date(schedule.start_time);
      const endTime = new Date(schedule.end_time);
      
      // 야간 근무 처리 (다음날까지)
      if (endTime < startTime) {
        endTime.setDate(endTime.getDate() + 1);
      }
      
      let workMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
      workMinutes -= breakTimeStringToMinutes(schedule.break_time);
      totalMinutes += Math.max(0, workMinutes); // 음수 방지
    }
  });
  
  return totalMinutes / 60; // 시간 단위로 변환
};

/**
 * 두 시간 사이의 시간 차이 계산 (시간 단위)
 */
export function getHoursBetween(start: Date, end: Date): number {
  const startTime = new Date(start);
  const endTime = new Date(end);
  
  // 야간 근무 처리 (다음날까지)
  if (endTime < startTime) {
    endTime.setDate(endTime.getDate() + 1);
  }
  
  return (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
}

/** 5인 이상 시급제 구간별 급여 breakdown (문서 8.3·8.5) */
export interface SegmentPayBreakdown {
  regularPay: number;
  overtimePay: number;
  nightPay: number;
  holidayPay: number;
  totalPay: number;
}

/**
 * 5인 이상 시급제 — 한 구간(segment) 분 단위 가산 계산 (문서 8.1·8.3)
 * workDate: 근무일(날짜만). 같은 날 판정·연장 누적용.
 */
export function computeSegmentPayBreakdown(
  segmentStart: Date,
  segmentEnd: Date,
  breakTimeStr: string | null | undefined,
  rate: number,
  workDate: Date,
  isHoliday: boolean
): SegmentPayBreakdown {
  const start = new Date(segmentStart);
  let end = new Date(segmentEnd);
  if (end <= start) {
    end.setDate(end.getDate() + 1);
  }
  const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);
  const breakMinutes = breakTimeStringToMinutes(breakTimeStr);
  const totalWorkMinutes = Math.min(
    Math.max(durationMinutes - breakMinutes, 0),
    MAX_WORK_MINUTES_PER_SEGMENT
  );

  const workY = workDate.getFullYear();
  const workM = workDate.getMonth();
  const workD = workDate.getDate();

  // 가산률 (Firestore SSOT, default 각 0.5). 분당 루프 외부에서 1회 read.
  const overtimeRates = useRatesStore.getState().current.labor?.overtime;
  const OT_RATE = overtimeRates?.weekday ?? 0.5;
  const NIGHT_RATE = overtimeRates?.night ?? 0.5;
  const HOLIDAY_RATE = overtimeRates?.holidayUnder8 ?? 0.5;

  const dayMinuteCount: Record<string, number> = {};
  let regularPay = 0;
  let overtimePay = 0;
  let nightPay = 0;
  let holidayPay = 0;
  let totalPay = 0;
  const basePerMin = rate / 60;

  for (let i = 0; i < totalWorkMinutes; i++) {
    const t = new Date(start.getTime() + i * 60 * 1000);
    const ty = t.getFullYear();
    const tm = t.getMonth();
    const td = t.getDate();
    const dayKey = `${ty}-${tm + 1}-${td}`;
    dayMinuteCount[dayKey] = (dayMinuteCount[dayKey] ?? 0) + 1;
    const sameDay = workY === ty && workM === tm && workD === td;
    const isOvertime = sameDay && (dayMinuteCount[dayKey] ?? 0) >= STANDARD_WORK_MINUTES_PER_DAY;
    const hour = t.getHours();
    const isNight = hour >= NIGHT_START_HOUR || hour < NIGHT_END_HOUR;

    regularPay += basePerMin;
    if (isOvertime) overtimePay += basePerMin * OT_RATE;
    if (isNight) nightPay += basePerMin * NIGHT_RATE;
    if (isHoliday) holidayPay += basePerMin * HOLIDAY_RATE;
    const mult =
      1 +
      (isOvertime ? OT_RATE : 0) +
      (isNight ? NIGHT_RATE : 0) +
      (isHoliday ? HOLIDAY_RATE : 0);
    totalPay += basePerMin * mult;
  }

  return {
    regularPay: Math.round(regularPay),
    overtimePay: Math.round(overtimePay),
    nightPay: Math.round(nightPay),
    holidayPay: Math.round(holidayPay),
    totalPay: Math.round(totalPay),
  };
}

/**
 * 5인 이상 시급제 — 구간 총 급여만 반환 (breakdown 불필요 시)
 */
export function computeSegmentPay(
  segmentStart: Date,
  segmentEnd: Date,
  breakTimeStr: string | null | undefined,
  rate: number,
  workDate: Date,
  isHoliday: boolean
): number {
  return computeSegmentPayBreakdown(
    segmentStart,
    segmentEnd,
    breakTimeStr,
    rate,
    workDate,
    isHoliday
  ).totalPay;
}

// 주간 근무 시간 계산 (일요일~토요일)
export const calculateWeeklyHours = (schedules: WorkSchedule[], weekStart: Date): number => {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  
  // 날짜만 비교하기 위해 시간 부분 제거
  const normalizeDate = (date: Date | string): Date => {
    const d = typeof date === 'string' ? new Date(date) : new Date(date);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  };
  
  const normalizedWeekStart = normalizeDate(weekStart);
  const normalizedWeekEnd = normalizeDate(weekEnd);
  
  const weekSchedules = schedules.filter(schedule => {
    const scheduleDate = normalizeDate(schedule.date);
    // 날짜만 비교 (시간 부분 무시)
    return scheduleDate >= normalizedWeekStart && scheduleDate <= normalizedWeekEnd;
  });
  
  return calculateTotalWorkHours(weekSchedules);
};

// 주휴수당 계산
export const calculateHolidayAllowance = (schedules: WorkSchedule[], employee: Employee, month: Date): number => {
  if (!employee.isHourly) return 0; // 시급제 직원만 주휴수당 지급
  
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  
  // 해당 월의 모든 주 찾기
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  
  let totalHolidayAllowance = 0;
  
  // 월의 첫 번째 일요일 찾기
  let currentWeekStart = new Date(firstDay);
  while (currentWeekStart.getDay() !== 0) {
    currentWeekStart.setDate(currentWeekStart.getDate() - 1);
  }
  
  // 주휴수당 최소 근로시간 (Firestore SSOT, default 15)
  const minRestHours =
    useRatesStore.getState().current.labor?.weeklyRestMinHours ?? 15;

  // 모든 주에 대해 주휴수당 계산
  while (currentWeekStart <= lastDay) {
    const weekHours = calculateWeeklyHours(schedules, currentWeekStart);

    if (weekHours >= minRestHours) { // 주 15시간 이상 근무
      const hourlyRate = employee.hourlyRate;
      const weeklyHolidayAllowance = (weekHours / 40) * 8 * hourlyRate;
      totalHolidayAllowance += weeklyHolidayAllowance;
    }

    currentWeekStart.setDate(currentWeekStart.getDate() + 7); // 다음 주로
  }
  
  return Math.round(totalHolidayAllowance);
};

export interface CalculateMonthlySalaryOptions {
  is5Plus?: boolean;
  getRateForDate?: (employeeId: string, date: Date) => number;
  /**
   * 노동절(5/1) 합산 토글. true 일 때만 5/1 스케줄을 일반 정산에서 분리하고
   * laborDayAllowance 를 totalSalary 에 합산.
   * 설계문서 §8.1.
   */
  laborDayIncluded?: boolean;
  /**
   * 노동절 가산 5인 이상 판정 (설계문서 §7: 전월 말일 기준).
   * 호출자가 별도 계산해 전달. 미전달 시 options.is5Plus 로 fallback.
   */
  laborDayIs5Plus?: boolean;
}

export interface CalculateMonthlySalaryResult {
  baseSalary: number;
  holidayAllowance: number;
  totalSalary: number;
  regularPay?: number;
  overtimePay?: number;
  nightPay?: number;
  holidayPay?: number;
  /** 노동절 수당 — 토글 ON 시 totalSalary 에 이미 합산됨. 5월 외에는 0. */
  laborDayAllowance: number;
  /** 노동절 분해 (토글 무관, 항상 계산). 5월 외에는 null. */
  laborDayBreakdown: LaborDayAllowanceBreakdown | null;
  /** 토글 상태 echo */
  laborDayIncluded: boolean;
}

// 월급 계산 (기본급 + 주휴수당). 5인 이상 시급제일 때 연장·야간·휴일 가산 포함
export function calculateMonthlySalary(
  employee: Employee,
  schedules: WorkSchedule[],
  month: Date,
  options?: CalculateMonthlySalaryOptions
): CalculateMonthlySalaryResult {
  let baseSalary = 0;
  let holidayAllowance = 0;
  let regularPay = 0;
  let overtimePay = 0;
  let nightPay = 0;
  let holidayPay = 0;
  const is5Plus = options?.is5Plus === true;

  // ───── 노동절 분기 ─────
  // 설계문서 §8.1 함정 방지:
  //   토글 ON일 때만 5/1 스케줄을 분리, OFF면 일반 스케줄에 그대로 둔다.
  //   (OFF인데도 분리하면 5/1 일당이 통째로 사라짐)
  const isLaborDayMonth = monthHasLaborDay(month);
  const laborDayIncluded = options?.laborDayIncluded === true;
  const laborDayIs5Plus = options?.laborDayIs5Plus ?? is5Plus;

  let laborDaySchedules: WorkSchedule[] = [];
  let workingSchedules: WorkSchedule[] = schedules;

  if (isLaborDayMonth) {
    laborDaySchedules = schedules.filter((s) => isLaborDay(new Date(s.date)));
    if (laborDayIncluded) {
      workingSchedules = schedules.filter((s) => !isLaborDay(new Date(s.date)));
    }
  }

  if (employee.isHourly) {
    if (is5Plus) {
      // 5인 이상 시급제: 구간별 분 단위 가산 (문서 8.3·8.5)
      for (const schedule of workingSchedules) {
        const scheduleDate = typeof schedule.date === 'string' ? new Date(schedule.date) : new Date(schedule.date);
        const workDate = new Date(scheduleDate.getFullYear(), scheduleDate.getMonth(), scheduleDate.getDate());
        const rate =
          options?.getRateForDate?.(employee.id, workDate) ??
          (schedule.hourlyRate !== undefined && schedule.hourlyRate !== null ? schedule.hourlyRate : employee.hourlyRate);
        const isHoliday = isPublicHoliday(workDate);

        const segments: { start: Date; end: Date }[] = [];
        if (schedule.workTime1) {
          const s = schedule.workTime1.start instanceof Date ? schedule.workTime1.start : new Date(schedule.workTime1.start);
          const e = schedule.workTime1.end instanceof Date ? schedule.workTime1.end : new Date(schedule.workTime1.end);
          segments.push({ start: s, end: e });
        }
        if (schedule.workTime2) {
          const s = schedule.workTime2.start instanceof Date ? schedule.workTime2.start : new Date(schedule.workTime2.start);
          const e = schedule.workTime2.end instanceof Date ? schedule.workTime2.end : new Date(schedule.workTime2.end);
          segments.push({ start: s, end: e });
        }
        if (segments.length === 0 && schedule.start_time && schedule.end_time) {
          const s = new Date(schedule.start_time);
          let e = new Date(schedule.end_time);
          if (e <= s) e.setDate(e.getDate() + 1);
          segments.push({ start: s, end: e });
        }

        for (const seg of segments) {
          const b = computeSegmentPayBreakdown(
            seg.start,
            seg.end,
            schedule.break_time,
            rate,
            workDate,
            isHoliday
          );
          regularPay += b.regularPay;
          overtimePay += b.overtimePay;
          nightPay += b.nightPay;
          holidayPay += b.holidayPay;
          baseSalary += b.totalPay;
        }
      }
    } else {
      // 5인 미만 시급제: 가산 없음 (문서 8.2)
      let totalBaseSalary = 0;
      workingSchedules.forEach((schedule) => {
        let scheduleHours = 0;
        if (schedule.workTime1 || schedule.workTime2) {
          if (schedule.workTime1) scheduleHours += getHoursBetween(schedule.workTime1.start, schedule.workTime1.end);
          if (schedule.workTime2) scheduleHours += getHoursBetween(schedule.workTime2.start, schedule.workTime2.end);
          scheduleHours -= breakTimeStringToHours(schedule.break_time);
        } else if (schedule.start_time && schedule.end_time) {
          const startTime = new Date(schedule.start_time);
          let endTime = new Date(schedule.end_time);
          if (endTime < startTime) endTime.setDate(endTime.getDate() + 1);
          scheduleHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
          scheduleHours -= breakTimeStringToHours(schedule.break_time);
        }
        const scheduleHourlyRate =
          schedule.hourlyRate !== undefined && schedule.hourlyRate !== null ? schedule.hourlyRate : employee.hourlyRate;
        totalBaseSalary += Math.max(0, scheduleHours) * scheduleHourlyRate;
      });
      baseSalary = totalBaseSalary;
    }

    holidayAllowance = calculateHolidayAllowance(workingSchedules, employee, month);
  } else {
    baseSalary = employee.monthlySalary;
  }

  // ───── 노동절 분해 (토글과 무관하게 항상 계산) ─────
  let laborDayBreakdown: LaborDayAllowanceBreakdown | null = null;
  let laborDayAllowance = 0;
  if (isLaborDayMonth) {
    const workedHoursOnLaborDay = laborDaySchedules.reduce(
      (sum, s) => sum + calculateScheduleWorkedHours(s),
      0
    );
    const effectiveRate = resolveEffectiveRate(employee);
    laborDayBreakdown = calculateLaborDayBreakdown({
      isHourly: employee.isHourly,
      is5Plus: laborDayIs5Plus,
      effectiveRate,
      workedHours: workedHoursOnLaborDay,
    });
    if (laborDayIncluded) {
      laborDayAllowance = laborDayBreakdown.total;
    }
  }

  const totalSalary = baseSalary + holidayAllowance + laborDayAllowance;

  const result: CalculateMonthlySalaryResult = {
    baseSalary: Math.round(baseSalary),
    holidayAllowance: Math.round(holidayAllowance),
    totalSalary: Math.round(totalSalary),
    laborDayAllowance: Math.round(laborDayAllowance),
    laborDayBreakdown,
    laborDayIncluded,
  };
  if (is5Plus && employee.isHourly) {
    result.regularPay = Math.round(regularPay);
    result.overtimePay = Math.round(overtimePay);
    result.nightPay = Math.round(nightPay);
    result.holidayPay = Math.round(holidayPay);
  }
  return result;
}

// 원천세 계산 (employee_data.json 참조)
export const calculateWithholdingTax = (totalSalary: number, dependents: number): number => {
  // 면제선 이하면 원천세 0원 (Firestore SSOT 우선, default 1,060,000 — Flutter 앱과 동일)
  const exemptionThreshold =
    useRatesStore.getState().current.withholding?.simplifiedExemptionThreshold ?? 1_060_000;
  if (totalSalary <= exemptionThreshold) {
    return 0;
  }
  
  const taxTable = employeeTaxTable as TaxTableEntry[];
  
  // 부양가족 수에 따른 컬럼 선택 (1-11)
  const dependentKey = Math.min(Math.max(dependents, 1), 11).toString();
  
  // 정확한 급여와 일치하는 항목 찾기 (콤마가 포함된 문자열로 저장됨)
  // 앱과 동일 매칭을 위해 로케일 고정 (en-US: 2,900,000 형식)
  const salaryWithComma = totalSalary.toLocaleString('en-US');
  let exactMatch = taxTable.find(entry => 
    entry[''] === salaryWithComma && entry[dependentKey] && entry[dependentKey] !== '-'
  );
  
  if (exactMatch && exactMatch[dependentKey] && exactMatch[dependentKey] !== '-') {
    // 콤마가 포함된 세금 금액을 파싱 (예: "74,350" -> 74350)
    const taxAmount = exactMatch[dependentKey].replace(/,/g, '');
    return parseInt(taxAmount);
  }
  
  // 정확한 매치가 없으면 선형 보간법 사용
  return calculateInterpolatedTax(totalSalary, dependents, taxTable);
};

// 선형 보간법을 사용한 원천세 계산
const calculateInterpolatedTax = (salary: number, dependents: number, taxTable: TaxTableEntry[]): number => {
  const dependentKey = Math.min(Math.max(dependents, 1), 11).toString();
  
  // 정렬된 테이블에서 해당 급여 범위 찾기 (콤마가 포함된 문자열 처리)
  const sortedTable = taxTable
    .filter(entry => 
      entry[''] && 
      entry[dependentKey] && 
      entry[dependentKey] !== '-' && 
      entry[dependentKey] !== ''
    )
    .sort((a, b) => {
      const salaryA = parseInt(a[''].replace(/,/g, ''));
      const salaryB = parseInt(b[''].replace(/,/g, ''));
      return salaryA - salaryB;
    });
  
  if (sortedTable.length === 0) return 0;
  
  // 가장 가까운 두 점 찾기
  let lowerBound = null;
  let upperBound = null;
  
  for (const entry of sortedTable) {
    const entrySalary = parseInt(entry[''].replace(/,/g, ''));
    if (entrySalary <= salary) {
      lowerBound = entry;
    } else {
      upperBound = entry;
      break;
    }
  }
  
  // 하한값만 있는 경우
  if (lowerBound && !upperBound) {
    const taxAmount = lowerBound[dependentKey].replace(/,/g, '');
    return parseInt(taxAmount);
  }
  
  // 상한값만 있는 경우
  if (!lowerBound && upperBound) {
    const taxAmount = upperBound[dependentKey].replace(/,/g, '');
    return parseInt(taxAmount);
  }
  
  // 두 값 모두 있는 경우 선형 보간
  if (lowerBound && upperBound) {
    const x1 = parseInt(lowerBound[''].replace(/,/g, ''));
    const y1 = parseInt(lowerBound[dependentKey].replace(/,/g, ''));
    const x2 = parseInt(upperBound[''].replace(/,/g, ''));
    const y2 = parseInt(upperBound[dependentKey].replace(/,/g, ''));
    
    // 선형 보간 공식: y = y1 + (y2 - y1) * (x - x1) / (x2 - x1)
    const interpolatedTax = y1 + (y2 - y1) * (salary - x1) / (x2 - x1);
    return Math.round(interpolatedTax);
  }
  
  return 0;
};

// 4대 보험 계산 — 앱 정리 요율과 동일 (실수령액·공제 일치)
// 국민연금 4.75%(9.5%/2), 건강 3.595%+장기요양(건강×12.81%), 고용보험 실제 공제 0.9%, 산재 직원 공제 0(사업주 0.65%만 별도)
/** 4대보험 가입 여부 (Flutter 1/0, 문자열 "1" 등 통일 판단) */
function isFourInsuranceEnabled(employee: Employee): boolean {
  const v = (employee as any).hasFourInsurance ?? (employee as any).has_four_insurance;
  return v === true || v === 1 || v === '1';
}

export const calculateInsurance = (employee: Employee, totalSalary: number) => {
  if (!isFourInsuranceEnabled(employee)) {
    return {
      pension: 0,
      health: 0,
      longTermCare: 0,
      employment: 0,
      industrial: 0,
      total: 0,
      breakdown: {
        pension: 0,
        health: 0,
        longTermCare: 0,
        employment: 0,
        industrial: 0
      }
    };
  }

  // 법정 요율 — Firestore SSOT (`config/rates/{year}/current.socialInsurance`) 우선, 미동기화 시 default fallback.
  // 직원별 예전 override 값은 의도적으로 무시 (앱 실수령액과 일치).
  const si = useRatesStore.getState().current.socialInsurance;
  const PENSION_RATE = si?.nps?.workerShare ?? 0.0475;
  const HEALTH_RATE = si?.nhi?.workerShare ?? si?.nhi?.employerShare ?? 0.03595;
  const LTC_RATE_OF_NHI = si?.nhi?.ltcRateOfNhi ?? 0.1281;
  const EI_WORKER_RATE = si?.ei?.unemployment?.worker ?? 0.009;

  const pension = Math.round(totalSalary * PENSION_RATE);
  const health = Math.round(totalSalary * HEALTH_RATE);
  const longTermCare = Math.round(health * LTC_RATE_OF_NHI);

  // 고용보험: 근로자 실제 공제 (Employee.basicEmploymentInsurance 기본값과는 별개로 store 값 사용)
  const employment = Math.round(totalSalary * EI_WORKER_RATE);

  // 산재보험: 근로자 공제에는 포함하지 않음. 사업주 0.65%는 4대보험 계산기 등에서만 사용
  const industrial = 0;

  return {
    pension,
    health,
    longTermCare,
    employment,
    industrial,
    total: pension + health + longTermCare + employment + industrial,
    breakdown: {
      pension,
      health,
      longTermCare,
      employment,
      industrial
    }
  };
};

// 순급여 계산
export const calculateNetSalary = (employee: Employee, totalSalary: number) => {
  const insurance = calculateInsurance(employee, totalSalary);
  const dependents = employee.dependents ?? 1;
  const withholdingTax = calculateWithholdingTax(totalSalary, dependents);
  const localTaxRatio =
    useRatesStore.getState().current.withholding?.localTaxOfWithholding ?? 0.1;
  const localTax = Math.round(withholdingTax * localTaxRatio); // 지방세 (원천징수액 × 10%)
  
  const netSalary = totalSalary - insurance.total - withholdingTax - localTax;

  // 디버깅용(선택): 앱과 동일 입력인지 확인하려면 콘솔에서 window.__DEBUG_PAYROLL_NET__ = true 설정
  if (typeof window !== 'undefined' && (window as any).__DEBUG_PAYROLL_NET__) {
  }
  
  return {
    insurance: {
      ...insurance,
      // longTermCare 포함된 breakdown 반환
      breakdown: {
        pension: insurance.breakdown.pension,
        health: insurance.breakdown.health,
        longTermCare: insurance.breakdown.longTermCare,
        employment: insurance.breakdown.employment,
        industrial: insurance.breakdown.industrial
      }
    },
    tax: {
      withholding: withholdingTax,
      local: localTax,
      total: withholdingTax + localTax
    },
    netSalary: Math.round(netSalary)
  };
};

// 주간별 주휴수당 상세 정보
export const getWeeklyHolidayAllowanceDetails = (
  schedules: WorkSchedule[], 
  employee: Employee, 
  month: Date
): WeeklyHolidayDetail[] => {
  if (!employee.isHourly) return [];
  
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  
  const weeklyDetails: WeeklyHolidayDetail[] = [];
  let weekNumber = 1;
  
  // 월의 첫 번째 일요일 찾기 (해당 월 1일이 포함된 주의 일요일)
  // 예: 10월 1일이 화요일이면, 9월 29일 일요일부터 시작
  let currentWeekStart = new Date(firstDay);
  while (currentWeekStart.getDay() !== 0) {
    currentWeekStart.setDate(currentWeekStart.getDate() - 1);
  }
  
  // 해당 월에 포함되는 모든 주차를 처리
  // 주의 시작(일요일)이 해당 월의 마지막 날 이전이면 해당 월에 포함
  while (currentWeekStart <= lastDay) {
    const weekEnd = new Date(currentWeekStart);
    weekEnd.setDate(weekEnd.getDate() + 6); // 일요일 + 6일 = 토요일
    
    // 이 주차가 해당 월과 겹치는지 확인
    // (주의 시작 <= 월의 마지막 날) && (주의 끝 >= 월의 첫 날)
    const weekOverlapsMonth = currentWeekStart <= lastDay && weekEnd >= firstDay;
    
    if (!weekOverlapsMonth) {
      currentWeekStart.setDate(currentWeekStart.getDate() + 7);
      continue;
    }
    
    const weekHours = calculateWeeklyHours(schedules, currentWeekStart);
    // 주휴 임계 (Firestore SSOT, default 15)
    const minRestHours =
      useRatesStore.getState().current.labor?.weeklyRestMinHours ?? 15;
    const isEligible = weekHours >= minRestHours;
    
    // 날짜만 비교하기 위해 시간 부분 제거
    const normalizeDate = (date: Date | string): Date => {
      const d = typeof date === 'string' ? new Date(date) : new Date(date);
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    };
    
    const normalizedWeekStart = normalizeDate(currentWeekStart);
    const normalizedWeekEnd = normalizeDate(weekEnd);
    
    const weekSchedules = schedules.filter(schedule => {
      const scheduleDate = normalizeDate(schedule.date);
      // 날짜만 비교 (시간 부분 무시)
      return scheduleDate >= normalizedWeekStart && scheduleDate <= normalizedWeekEnd;
    });
    
    const workDays = weekSchedules.map(schedule => {
      // 템플릿 기반 스케줄 처리 (workTime1/workTime2 우선)
      let startTime: Date;
      let endTime: Date;
      let totalWorkHours = 0;
      
      if (schedule.workTime1 || schedule.workTime2) {
        // 템플릿 기반 스케줄: workTime1과 workTime2의 총 근무시간 계산
        if (schedule.workTime1) {
          const st1 = schedule.workTime1.start instanceof Date 
            ? schedule.workTime1.start 
            : new Date(schedule.workTime1.start);
          const et1 = schedule.workTime1.end instanceof Date 
            ? schedule.workTime1.end 
            : new Date(schedule.workTime1.end);
          totalWorkHours += getHoursBetween(st1, et1);
          // 표시용으로 첫 번째 시간 구간 사용
          if (!startTime) {
            startTime = st1;
            endTime = et1;
          }
        }
        
        if (schedule.workTime2) {
          const st2 = schedule.workTime2.start instanceof Date 
            ? schedule.workTime2.start 
            : new Date(schedule.workTime2.start);
          const et2 = schedule.workTime2.end instanceof Date 
            ? schedule.workTime2.end 
            : new Date(schedule.workTime2.end);
          totalWorkHours += getHoursBetween(st2, et2);
          // 표시용으로 마지막 종료 시간 사용
          if (endTime) {
            endTime = et2;
          } else {
            startTime = st2;
            endTime = et2;
          }
        }
      } else if (schedule.start_time && schedule.end_time) {
        // 기존 구조 (start_time/end_time)
        startTime = new Date(schedule.start_time);
        endTime = new Date(schedule.end_time);
        
        // 종료 시간이 시작 시간보다 이른 경우 (다음날로 처리)
        if (endTime < startTime) {
          endTime.setDate(endTime.getDate() + 1);
        }
        
        const duration = endTime.getTime() - startTime.getTime();
        totalWorkHours = duration / (1000 * 60 * 60);
      } else {
        // 시간 정보가 없는 경우
        return {
          date: normalizeDate(schedule.date),
          startTime: '',
          endTime: '',
          workHours: 0,
          breakTime: schedule.break_time || ''
        };
      }
      
      totalWorkHours -= breakTimeStringToHours(schedule.break_time);
      
      // 시간 표시 형식 (템플릿 기반인 경우 여러 구간 표시)
      let timeDisplay = '';
      if (schedule.workTime1) {
        const st1 = schedule.workTime1.start instanceof Date 
          ? schedule.workTime1.start 
          : new Date(schedule.workTime1.start);
        const et1 = schedule.workTime1.end instanceof Date 
          ? schedule.workTime1.end 
          : new Date(schedule.workTime1.end);
        timeDisplay = `${st1.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} - ${et1.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`;
        
        if (schedule.workTime2) {
          const st2 = schedule.workTime2.start instanceof Date 
            ? schedule.workTime2.start 
            : new Date(schedule.workTime2.start);
          const et2 = schedule.workTime2.end instanceof Date 
            ? schedule.workTime2.end 
            : new Date(schedule.workTime2.end);
          timeDisplay += ` / ${st2.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} - ${et2.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`;
        }
      } else {
        timeDisplay = `${startTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })} - ${endTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' })}`;
      }
      
      return {
        date: normalizeDate(schedule.date),
        startTime: timeDisplay.includes(' / ') ? '' : startTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
        endTime: timeDisplay.includes(' / ') ? '' : endTime.toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
        workHours: Math.round(Math.max(0, totalWorkHours) * 10) / 10,
        breakTime: schedule.break_time || '',
        timeDisplay: timeDisplay.includes(' / ') ? timeDisplay : undefined
      };
    }).filter(day => day.workHours > 0 || day.startTime || day.timeDisplay); // 근무시간이 0보다 큰 것만 포함
    
    const holidayAllowance = isEligible ? (weekHours / 40) * 8 * employee.hourlyRate : 0;
    
    weeklyDetails.push({
      weekNumber,
      weekStart: new Date(currentWeekStart),
      weekEnd: new Date(weekEnd),
      weekStartFormatted: `${String(currentWeekStart.getMonth() + 1).padStart(2, '0')}/${String(currentWeekStart.getDate()).padStart(2, '0')}`,
      weekEndFormatted: `${String(weekEnd.getMonth() + 1).padStart(2, '0')}/${String(weekEnd.getDate()).padStart(2, '0')}`,
      totalWorkHours: Math.round(weekHours * 10) / 10,
      isEligible,
      holidayAllowance: Math.round(holidayAllowance),
      workDays,
      workDaysCount: workDays.length
    });
    
    currentWeekStart.setDate(currentWeekStart.getDate() + 7);
    weekNumber++;
  }
  
  return weeklyDetails;
};

// 순급여 계산 (보험, 세금 계산 포함)
// 이 함수는 insurance, tax, netSalary만 계산합니다.
export const calculatePayrollDetail = (
  employee: Employee, 
  totalSalary: number
): {
  insurance: ReturnType<typeof calculateInsurance>;
  tax: ReturnType<typeof calculateNetSalary>['tax'];
  netSalary: number;
} => {
  const payrollDetail = calculateNetSalary(employee, totalSalary);
  
  return {
    insurance: payrollDetail.insurance,
    tax: payrollDetail.tax,
    netSalary: payrollDetail.netSalary
  };
};
